(() => {
  'use strict';
  if (customElements.get('evilko-cart')) return;

  class EvilkoCart extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      try {
        this.config = JSON.parse(this.querySelector('[data-cart-config]').textContent);
      } catch (_) { return; }
      this.ready = true;
      this.busy = false;
      this.codes = this.readCodes();
      this.querySelector('[data-cart-discount]')?.removeAttribute('name');
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('[data-cart-discount]')) {
          event.preventDefault();
          this.applyCode();
        }
      });
      this.addEventListener('submit', (event) => this.onSubmit(event));
    }

    message(key, error = false, detail = '') {
      const status = this.querySelector('[data-cart-status]');
      if (!status) return;
      status.hidden = false;
      status.textContent = detail || this.config.copy[key] || this.config.copy.error || '';
      status.classList.toggle('is-error', error);
      status.dataset.state = error ? 'error' : 'success';
    }

    readCodes(root = this) {
      return [...new Set(Array.from(root.querySelectorAll('[data-cart-code]'), (node) => node.dataset.cartCode).filter(Boolean))];
    }

    quantityInput(key) {
      return Array.from(this.querySelectorAll('[data-cart-quantity]')).find((input) => input.dataset.lineKey === key);
    }

    quantity(input) {
      const raw = input.value.trim();
      const number = Number(raw);
      const minimum = Number(input.dataset.minimum || input.min || 0);
      const maximum = input.max === '' ? Infinity : Number(input.max);
      const step = Number(input.step || 1);
      if (raw === '' || !Number.isSafeInteger(number) || number < 0 || number > maximum || (number !== 0 && (number < minimum || number % step !== 0))) return null;
      return number;
    }

    onClick(event) {
      const control = event.target.closest('[data-cart-step],[data-cart-remove],[data-cart-apply],[data-cart-remove-code]');
      if (!control || !this.contains(control)) return;
      event.preventDefault();
      if (this.busy) return;
      if (control.hasAttribute('data-cart-apply')) return this.applyCode();
      if (control.hasAttribute('data-cart-remove-code')) {
        const code = control.dataset.cartRemoveCode;
        return this.mutate('update', {}, control, '', code);
      }
      const key = control.dataset.lineKey;
      const input = this.quantityInput(key);
      if (!input) return;
      if (control.hasAttribute('data-cart-remove')) return this.changeQuantity(input, 0, control);
      const current = this.quantity(input);
      if (current === null) return this.invalidQuantity(input);
      const next = current + Number(control.dataset.cartStep) * Number(input.step || 1);
      const previous = input.value;
      input.value = String(next);
      if (this.quantity(input) === null) {
        input.value = previous;
        return this.invalidQuantity(input);
      }
      this.changeQuantity(input, next, control);
    }

    onChange(event) {
      const input = event.target;
      if (!input.matches('[data-cart-quantity]') || this.busy) return;
      const quantity = this.quantity(input);
      if (quantity === null) return this.invalidQuantity(input);
      if (quantity !== Number(input.dataset.quantity)) this.changeQuantity(input, quantity, input);
    }

    invalidQuantity(input) {
      input.value = input.dataset.quantity;
      this.message('quantity_invalid', true);
      input.focus({ preventScroll: true });
    }

    onSubmit(event) {
      if (!event.target.matches('[data-cart-form]')) return;
      if (this.busy) {
        event.preventDefault();
        this.message('updating');
        return;
      }
      const inputs = Array.from(this.querySelectorAll('[data-cart-quantity]'));
      const invalid = inputs.find((input) => this.quantity(input) === null);
      if (invalid) {
        event.preventDefault();
        this.invalidQuantity(invalid);
        return;
      }
    }

    changeQuantity(input, quantity, control) {
      return this.mutate('change', { id: input.dataset.lineKey, quantity }, control);
    }

    applyCode() {
      if (this.busy) return;
      const input = this.querySelector('[data-cart-discount]');
      const code = input?.value.trim();
      if (!code) {
        this.message('discount_invalid', true);
        input?.focus({ preventScroll: true });
        return;
      }
      return this.mutate('update', {}, input, code);
    }

    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('button,input,select').forEach((control) => {
        if (busy) {
          if (!control.disabled) {
            control.dataset.cartBusyDisabled = 'true';
            control.disabled = true;
          }
        } else if (control.dataset.cartBusyDisabled) {
          control.disabled = false;
          delete control.dataset.cartBusyDisabled;
        }
      });
      this.querySelectorAll('[data-cart-remove]').forEach((control) => {
        if (busy) control.setAttribute('aria-disabled', 'true');
        else control.removeAttribute('aria-disabled');
      });
    }

    focusToken(control) {
      if (!control) return null;
      return {
        key: control.dataset.lineKey,
        step: control.dataset.cartStep,
        remove: control.hasAttribute('data-cart-remove'),
        discount: control.hasAttribute('data-cart-discount') || control.hasAttribute('data-cart-apply'),
      };
    }

    restoreFocus(token) {
      if (!token) return;
      let target;
      if (token.discount) target = this.querySelector('[data-cart-discount]');
      else if (token.key) {
        const candidates = Array.from(this.querySelectorAll('[data-line-key]')).filter((node) => node.dataset.lineKey === token.key);
        target = candidates.find((node) => token.step !== undefined ? node.dataset.cartStep === token.step : token.remove ? node.hasAttribute('data-cart-remove') : node.hasAttribute('data-cart-quantity'));
      }
      target ||= this.querySelector('[data-cart-quantity],[data-cart-empty-link]');
      target?.focus({ preventScroll: true });
    }

    endpoint(value) {
      const url = new URL(value, window.location.origin);
      if (!url.pathname.endsWith('.js')) url.pathname = url.pathname.replace(/\/$/, '') + '.js';
      return url.href;
    }

    async sectionHTML(data) {
      const bundled = data?.sections?.[this.config.sectionId];
      if (typeof bundled === 'string' && bundled.trim()) return bundled;
      const url = new URL(this.config.cartUrl, window.location.origin);
      url.searchParams.set('section_id', this.config.sectionId);
      const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Section could not be loaded.');
      return response.text();
    }

    async render(data) {
      const html = await this.sectionHTML(data);
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.querySelector('evilko-cart [data-cart-content]');
      const content = this.querySelector('[data-cart-content]');
      if (!replacement || !content) throw new Error('Cart section is unavailable.');
      content.replaceWith(replacement);
      this.querySelector('[data-cart-discount]')?.removeAttribute('name');
      const configNode = parsed.querySelector('evilko-cart [data-cart-config]');
      let fresh = {};
      try { fresh = JSON.parse(configNode?.textContent || '{}'); } catch (_) {}
      const count = Number(data?.item_count ?? fresh.itemCount);
      if (Number.isFinite(count)) {
        document.querySelectorAll('[data-en-cart-count]').forEach((node) => { node.textContent = String(count); });
        document.querySelectorAll('[data-en-cart-label]').forEach((node) => {
          const template = node.dataset.labelTemplate;
          if (template) node.setAttribute('aria-label', template.replace('__COUNT__', String(count)));
        });
      }
      if (Array.isArray(data?.discount_codes)) {
        this.codes = data.discount_codes.filter((entry) => entry.applicable !== false).map((entry) => entry.code).filter(Boolean);
      } else this.codes = this.readCodes();
      this.setBusy(true);
    }

    async mutate(kind, payload, control, requestedCode = '', removedCode = '') {
      if (this.busy) return;
      const focus = this.focusToken(control);
      this.setBusy(true);
      this.message('updating');
      let didRender = false;
      try {
        if (kind === 'update') {
          const snapshot = await fetch(this.endpoint(this.config.cartUrl), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
          if (!snapshot.ok) throw new Error(this.config.copy.error);
          const cart = await snapshot.json();
          const active = Array.isArray(cart.discount_codes) ? cart.discount_codes.filter((entry) => entry.applicable !== false).map((entry) => entry.code).filter(Boolean) : this.codes;
          const exclude = (requestedCode || removedCode).toLowerCase();
          const retained = active.filter((code) => code.toLowerCase() !== exclude);
          if (requestedCode) retained.push(requestedCode);
          payload = { discount: [...new Set(retained)].join(',') };
        }
        const response = await fetch(this.endpoint(kind === 'change' ? this.config.changeUrl : this.config.updateUrl), {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...payload, sections: [this.config.sectionId], sections_url: new URL(this.config.cartUrl, window.location.origin).pathname }),
        });
        const data = await response.json();
        if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : this.config.copy.error);
        await this.render(data);
        didRender = true;
        if (requestedCode) {
          const applied = data.discount_codes?.find((entry) => entry.code.toLowerCase() === requestedCode.toLowerCase());
          if (applied?.applicable !== true) this.message('discount_invalid', true);
          else if (!this.readCodes().some((code) => code.toLowerCase() === requestedCode.toLowerCase())) this.message('discount_shipping');
          else this.message('discount_applied');
        } else this.message('updated');
      } catch (error) {
        if (!didRender) {
          try { await this.render(null); }
          catch (_) { window.location.reload(); }
        }
        this.message('error', true, error.message);
      } finally {
        this.setBusy(false);
        this.restoreFocus(focus);
      }
    }
  }

  customElements.define('evilko-cart', EvilkoCart);
})();
